import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ShieldIcon, UploadCloudIcon, XIcon, FileIcon } from 'lucide-react';
// CIMB pinned to the top, then alphabetical
const BANKS = ['CIMB', 'AmBank', 'Bank Islam Malaysia', 'CIMB Bank', 'Hong Leong Bank', 'HSBC Malaysia', 'Maybank', 'Public Bank', 'RHB Bank', 'Standard Chartered Malaysia', 'UOB Malaysia'];
interface SimulatedFile {
  name: string;
  size: number;
}
export function UpdatePayoutAccount() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bank, setBank] = useState('CIMB');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [file, setFile] = useState<SimulatedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const accountsMatch = accountNumber.length > 0 && accountNumber === confirmAccountNumber;
  const showMismatch = confirmAccountNumber.length > 0 && accountNumber !== confirmAccountNumber;
  const isValid = bank !== '' && accountNumber.length >= 6 && accountsMatch && file !== null;
  const goBack = () => navigate('/settings?tab=payout');
  const simulateUpload = () => {
    const today = new Date().toISOString().slice(0, 10);
    setFile({
      name: `Bank_Statement_${today}.pdf`,
      size: 2457600
    });
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    simulateUpload();
  };
  const handleSubmit = () => {
    if (!isValid) return;
    setSubmitting(true);
    const requestId = `PAYOUT-REQ-${Date.now()}`;
    const payload = {
      id: requestId,
      bank,
      accountNumber,
      maskedAccount: `••••${accountNumber.slice(-4)}`,
      fileName: file?.name,
      submittedAt: new Date().toISOString(),
      status: 'Pending'
    };
    try {
      sessionStorage.setItem('pendingPayoutUpdate', JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
    setTimeout(() => {
      setSubmitting(false);
      navigate('/settings?tab=payout', {
        state: {
          payoutUpdateSubmitted: true,
          requestId
        }
      });
    }, 600);
  };
  return <div className="min-h-full bg-gray-50" data-id="element-11277" data-mp="30jmy05xaiv3e">
      <div className="max-w-3xl mx-auto px-6 py-8 pb-32" data-id="element-11278" data-mp="3f9u3ll278jmo">
        <button onClick={goBack} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6 group" data-id="element-11279" data-mp="3f89a7yrmf6qe">
          <ChevronLeftIcon size={16} className="mr-1 group-hover:-translate-x-0.5 transition-transform" data-id="element-11280" data-mp="3o9r028utymsa" />
          Back to Payout Settings
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-11281" data-mp="1tvzj0oa690mt">
          Update Payout Account (MYR)
        </h1>

        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8" data-id="element-11282" data-mp="1pwh72i0bvzw4">
          <ShieldIcon size={18} className="text-blue-700 flex-shrink-0 mt-0.5" data-id="element-11283" data-mp="1fsipeqt2n72v" />
          <p className="text-sm text-blue-900 leading-relaxed" data-id="element-11284" data-mp="1js159861cryu">
            <span className="font-semibold" data-id="element-11285" data-mp="160cvvcn68hlk">Security Notice:</span> To protect
            your funds, updating your settlement account requires a standard
            compliance review. Your current account (CIMB ****5678) will
            continue receiving payouts until the new account is approved.
          </p>
        </div>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6" data-id="element-11286" data-mp="113mgysn3rs1q">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-11287" data-mp="n44oo3allwnm">
            New Bank Details
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-11288" data-mp="s1wnlh9nuj1b">
            Enter the bank account where you want to receive future payouts.
          </p>

          <div className="space-y-4" data-id="element-11289" data-mp="2mg7dlt1zrj8o">
            <div data-id="element-11290" data-mp="2mf5sevr9hgd8">
              <label htmlFor="bank" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11291" data-mp="2mdl6tvmxd7ri">
                Select your bank
              </label>
              <select id="bank" value={bank} onChange={e => setBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]" data-id="element-11292" data-mp="hc8pui0g8oxy">
                <optgroup label="Recommended" data-id="element-11293" data-mp="3usy9373ebgiu">
                  <option value="CIMB" data-id="element-11294" data-mp="3ur53zw8fl4cy">CIMB</option>
                </optgroup>
                <optgroup label="All banks (A–Z)" data-id="element-11295" data-mp="3puodwwho8fx8">
                  {BANKS.filter(b => b !== 'CIMB').map(b => <option key={b} value={b} data-id="element-11296" data-mp="3ksz39hyd249o">
                      {b}
                    </option>)}
                </optgroup>
              </select>
            </div>

            <div data-id="element-11297" data-mp="3bv5in49ykz2c">
              <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11298" data-mp="3bwq2nzynczly">
                Account Number
              </label>
              <input id="account" type="text" inputMode="numeric" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} onFocus={() => {
              if (accountNumber === '' && confirmAccountNumber === '') {
                setAccountNumber('7730001234567');
                setConfirmAccountNumber('7730001234567');
              }
            }} placeholder="e.g. 7730001234567" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]" data-id="element-11299" data-mp="1wd8yk48378mj" />
            </div>

            <div data-id="element-11300" data-mp="1x7uzuenzbi6y">
              <label htmlFor="confirm-account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-11301" data-mp="1x7bf6q6y2fp8">
                Confirm Account Number
              </label>
              <input id="confirm-account" type="text" inputMode="numeric" value={confirmAccountNumber} onChange={e => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder="Re-enter account number" className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${showMismatch ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]'}`} data-id="element-11302" data-mp="260etf0peywkx" />
              {showMismatch && <p className="mt-1.5 text-xs text-red-600" data-id="element-11303" data-mp="2tpzmicfa7a7x">
                  Account numbers do not match.
                </p>}
            </div>

            <p className="text-xs text-gray-500 pt-1" data-id="element-11304" data-mp="325gfglmr8a9v">
              The account name must exactly match your registered business name.
            </p>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-11305" data-mp="2s9vhqzopxw5x">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-11306" data-mp="2x8q060irxipi">
            Verification Document
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-11307" data-mp="3kxreg4eafo2p">
            Please upload a recent bank statement or a letter of confirmation
            from your bank.
          </p>

          {!file ? <div onClick={simulateUpload} onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={onDrop} className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-[var(--brand-primary)] bg-[var(--brand-primarylight)]' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            simulateUpload();
          }
        }} data-id="element-11308" data-mp="3b6wqmvle8gf5">
              <UploadCloudIcon size={32} className="mx-auto text-gray-400 mb-3" data-id="element-11309" data-mp="2ridj6a9xiu3q" />
              <p className="text-sm font-medium text-gray-700" data-id="element-11310" data-mp="2ml55omzq7e8t">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11311" data-mp="2djxh4wn0hrrx">
                PDF, JPG, or PNG up to 10MB
              </p>
            </div> : <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-11312" data-mp="1756asdf4deqi">
              <div className="flex items-center gap-3 min-w-0" data-id="element-11313" data-mp="12378l6v48nv5">
                <div className="w-10 h-10 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-11314" data-mp="3dswtc2rp95xs">
                  <FileIcon size={18} className="text-gray-500" data-id="element-11315" data-mp="3ioubmemiwmd7" />
                </div>
                <div className="min-w-0" data-id="element-11316" data-mp="3mqyzsxu7cqsg">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-11317" data-mp="3rj0boingkfhy">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-11318" data-mp="bw7dvip77d8">
                    {(file.size / 1024 / 1024).toFixed(2)} MB · Simulated upload
                  </p>
                </div>
              </div>
              <button onClick={() => setFile(null)} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0" aria-label="Remove file" data-id="element-11319" data-mp="ac6m5kkdsmui">
                <XIcon size={16} className="text-gray-500" data-id="element-11320" data-mp="2c42vcnj2tkm4" />
              </button>
            </div>}

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" readOnly data-id="element-11321" data-mp="2g4d94md41fbj" />

          <p className="text-xs text-gray-500 mt-4" data-id="element-11322" data-mp="3vq0cbn4c6h94">
            Must clearly show the bank logo, your business name, and the new
            account number. Max size: 10MB (PDF, JPG, PNG).
          </p>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-[var(--sidebar-w,240px)] bg-white border-t border-gray-200 z-10" data-id="element-11323" data-mp="egvajmi3gdt3">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-11324" data-mp="igce2adyn0vm">
          <button onClick={goBack} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-11325" data-mp="n9f448jj3s72">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!isValid || submitting} className="px-5 py-2.5 text-sm font-semibold rounded-md transition-colors disabled:cursor-not-allowed" style={{
          backgroundColor: isValid && !submitting ? 'var(--brand-primary)' : '#F3F4F6',
          color: isValid && !submitting ? '#ffffff' : '#9CA3AF'
        }} onMouseEnter={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = 'var(--brand-primaryhover)';
        }} onMouseLeave={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = 'var(--brand-primary)';
        }} data-id="element-11326" data-mp="x4qiuquxszet">
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>;
}